import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Logger } from '@se/se.platform.base';
import EstensaModel, { EWebViewerType } from 'estensa-module';
import { Subscription } from 'rxjs';
import { EllipsisWithTooltipDirective } from '../../../../directives/ellipsis-with-tooltip.directive';
import { IRadioEprEventEx } from '../../../../models/patient-history.models';
import { seTimelineComponentSetSelectedItem } from '../../../../se-timeline/redux/se-timeline.actions';
import { EstensaHealthPlatformService } from '../../../../services/angularjs/health-platform.service';
import { ImagingService } from '../../../../services/imaging/imaging.service';
import {
    NotificationService,
} from '../../../../services/notification.service';
import { UserConfigurationSettingsService } from '../../../../services/settings/user-configuration-settings.service';
import { EstensaWaitingService } from '../../../../services/waiting.service';
import { HistoryItemHelperService } from '../../services/history-item-helper.service';

@Component({
    selector: 'se-side-patient-history-item',
    templateUrl: './se-side-patient-history-item.component.html',
    styleUrls: ['./se-side-patient-history-item.component.scss'],
    animations: [
        trigger('expansionAnimation', [
            transition(':enter', [style({ opacity: 0 }), animate('400ms ease-in', style({ opacity: 1 }))]),
            transition(':leave', [animate('400ms ease-out', style({ opacity: 0 }))]),
        ]),
    ],
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        EllipsisWithTooltipDirective,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeSidePatientHistoryItemComponent implements OnInit, AfterViewChecked {
    @ViewChild('card') selectedCard!: ElementRef;
    @Input() item!: IRadioEprEventEx;
    @Input() expandedItemId: string | null = null;
    @Input() isCp: boolean = false;
    @Output() historySelectionChanged = new EventEmitter<IRadioEprEventEx>();
    @HostBinding('style') style = {};

    imagesLoades: boolean = false;
    isAuthorized: boolean = true;
    timelineItemSelectionSubscription?: Subscription;

    private lastExpandedItemId: string | null = null;

    get isExpanded(): boolean {
        return this.expandedItemId === this.item.id;
    }

    constructor(
        private healthPlatformService: EstensaHealthPlatformService,
        private imagingService: ImagingService,
        private logger: Logger,
        private notificationService: NotificationService,
        private translate: TranslateService,
        private actions: Actions,
        private waitingService: EstensaWaitingService,
        private userConfigurationSettingsService: UserConfigurationSettingsService,
        private historyItemHelperService: HistoryItemHelperService
    ) { }

    ngOnInit() {
        this.listenForStoreEvents();
        this.isAuthorized = this.userConfigurationSettingsService.isAuthorized(['viewer_Get'], 'single');
    }

    ngAfterViewChecked() {
        if (this.isExpanded && this.lastExpandedItemId !== this.item.id) {
            this.lastExpandedItemId = this.item.id;
            setTimeout(() => {
                this.selectedCard?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        }
    }

    toggleExpanded() {
        if (this.isExpanded) {
            this.historySelectionChanged.emit({ ...this.item, selected: false });
        } else {
            this.historySelectionChanged.emit(this.item);
        }
    }

    onCardSelected() {
        if (!this.item.selected) {
            this.item.selected = true;
            this.historySelectionChanged.emit(this.item);
        }
    }

    getReportDateString() {
        return this.translate.instant('ReportDate') + ': ' + this.item.reportDateString;
    }

    openPdf(event) {
        this.onCardSelected();
        this.historyItemHelperService.openPdf(event, this.item);
    }

    async openImagesByEprEvent(event) {
        this.onCardSelected();
        this.historyItemHelperService.openImagesByEprEvent(event, this.item);
    }

    async checkViewerAppendCapability() { // NON VIENE USATO??
        let type = await this.imagingService.getCurrentViewerType();
        switch (type) {
            // Qui tutti i visualizzatori che supportano l'append di immagini
            case EWebViewerType.eUnity:
            case EWebViewerType.EstensaReview:
            case EWebViewerType.EstensaReviewVNext:
            case EWebViewerType.SeWebViewer:
            case EWebViewerType.ZEfiRO:
                return;
            default:
                this.logger.warn(
                    `Viewer ${type} is not able to handle multiple studies images concatenation, but reportImageActionType setting value is 'Ask' or 'Append'`,
                );
        }
    }

    openParameters(event) {
        event.stopPropagation();

        this.onCardSelected();

        const healthPatient: EstensaModel.HealthPlatformPatient = {
            id: this.item.patient.id,
            taxCode: this.item.patient.taxCode,
        };

        this.waitingService.isWaiting = true;

        this.healthPlatformService
            .openPageAsync(EstensaModel.HealthPlatformOperation.MEASURES, healthPatient)
            .catch((e) => {
                this.notificationService.httpEstensaFaultError(
                    e,
                    this.translate.instant('Error'),
                    this.translate.instant('Exception_HealthPlatformFault'),
                );
            })
            .finally(() => {
                this.waitingService.isWaiting = false;
            });
    }

    //NB rimosso perchè in alcuni casi si visualizzava solo l'icona di pdf dentro la scheda ma data, descrizione e qualche altro dato erano di fatto presenti
    areDataInExpanderPresent(item: IRadioEprEventEx): boolean {
        return !!item.businessUnit || !!item.archiveNumber || !!item.admissionNumber || !!item.externalAdmissionNumber;
    }

    listenForStoreEvents() {
        this.timelineItemSelectionSubscription = this.actions
            .pipe(ofType(seTimelineComponentSetSelectedItem))
            .subscribe((action) => {
                if (action.selectedItem === this.item.id) {
                    this.item.selected = true;
                    this.selectedCard.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    this.historySelectionChanged.emit(this.item);
                }
            });
    }

    copyReport(event: any, item: IRadioEprEventEx) {
        this.historyItemHelperService.copyReport(event, item);
    }
}
